import React from "react";
import { ArrowLeft, Shield, FileText, RefreshCw, Scale } from "lucide-react";
import { Address, LocationSettings } from "../types";
import { formatAddress } from "../utils/utils";
import { useTranslation } from "react-i18next";

interface LegalPagesProps {
  pageType: "privacy" | "terms" | "refund" | "legal";
  onBack: () => void;
  locationSettings: LocationSettings | null;
}

const LegalPages: React.FC<LegalPagesProps> = ({
  pageType,
  onBack,
  locationSettings,
}) => {
  const { t, i18n } = useTranslation();
  const getPageContent = () => {
    switch (pageType) {
      case "privacy":
        if (i18n.language === "fr") {
          return {
            title: "Politique de Confidentialité",
            icon: Shield,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Collecte des Informations
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Chez Manager Food, nous collectons les informations
                      suivantes pour vous offrir le meilleur service possible :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Informations personnelles :</strong> Nom,
                        prénom, adresse e-mail, numéro de téléphone
                      </li>
                      <li>
                        <strong>Informations de livraison :</strong> Adresse
                        complète, instructions spéciales
                      </li>
                      <li>
                        <strong>Informations de paiement :</strong> Données de
                        carte bancaire (traitées de manière sécurisée par
                        Stripe)
                      </li>
                      <li>
                        <strong>Données d'utilisation :</strong> Historique des
                        commandes, préférences alimentaires
                      </li>
                      <li>
                        <strong>Données techniques :</strong> Adresse IP, type
                        de navigateur, données de géolocalisation (avec votre
                        consentement)
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Utilisation des Données
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>Nous utilisons vos données personnelles pour :</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Traiter et livrer vos commandes</li>
                      <li>Vous contacter concernant votre commande</li>
                      <li>
                        Améliorer nos services et personnaliser votre expérience
                      </li>
                      <li>Vous envoyer des notifications importantes</li>
                      <li>Respecter nos obligations légales</li>
                      <li>Prévenir la fraude et assurer la sécurité</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Partage des Données
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Nous ne vendons jamais vos données personnelles. Nous
                      pouvons partager vos informations uniquement avec :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Partenaires de livraison :</strong> Pour assurer
                        la livraison de vos commandes
                      </li>
                      <li>
                        <strong>Processeurs de paiement :</strong> Stripe pour
                        traiter vos paiements de manière sécurisée
                      </li>
                      <li>
                        <strong>Prestataires de services :</strong> Pour
                        l'hébergement, l'analyse et le support client
                      </li>
                      <li>
                        <strong>Autorités légales :</strong> Si requis par la
                        loi
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Sécurité des Données
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Nous mettons en place des mesures de sécurité robustes
                      pour protéger vos données :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Chiffrement SSL/TLS pour toutes les transmissions</li>
                      <li>
                        Stockage sécurisé avec chiffrement des données sensibles
                      </li>
                      <li>
                        Accès limité aux données par le personnel autorisé
                      </li>
                      <li>Surveillance continue des systèmes</li>
                      <li>Audits de sécurité réguliers</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Vos Droits
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Conformément aux lois sur la protection des données, vous
                      avez le droit de :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Accès :</strong> Demander une copie de vos
                        données personnelles
                      </li>
                      <li>
                        <strong>Rectification :</strong> Corriger des données
                        inexactes
                      </li>
                      <li>
                        <strong>Suppression :</strong> Demander la suppression
                        de vos données
                      </li>
                      <li>
                        <strong>Portabilité :</strong> Recevoir vos données dans
                        un format structuré
                      </li>
                      <li>
                        <strong>Opposition :</strong> Vous opposer au traitement
                        de vos données
                      </li>
                      <li>
                        <strong>Limitation :</strong> Demander la limitation du
                        traitement
                      </li>
                    </ul>
                    <p className="mt-4">
                      Pour exercer ces droits, contactez-nous à :{" "}
                      <strong>managerfood25@gmail.com</strong>
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Cookies et Technologies Similaires
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Nous utilisons des cookies pour améliorer votre expérience
                      :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Cookies essentiels :</strong> Nécessaires au
                        fonctionnement du site
                      </li>
                      <li>
                        <strong>Cookies de performance :</strong> Pour analyser
                        l'utilisation du site
                      </li>
                      <li>
                        <strong>Cookies de préférence :</strong> Pour mémoriser
                        vos choix
                      </li>
                    </ul>
                    <p>
                      Vous pouvez gérer vos préférences de cookies dans les
                      paramètres de votre navigateur.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Pour toute question concernant cette politique de
                      confidentialité, contactez-nous :
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Manager Food</strong>
                      </p>
                      <p>Email : managerfood25@gmail.com</p>
                      <p>Téléphone : 581-580-5573</p>
                      <p>
                        {" "}
                        {`Adresse : ${
                          locationSettings !== null
                            ? formatAddress({
                                id: locationSettings.id,
                                street: locationSettings.street,
                                city: locationSettings.city,
                                country: locationSettings.country,
                                zipcode: locationSettings.postalCode,
                                latitude: locationSettings.latitude,
                                longitude: locationSettings.longitude,
                                province: locationSettings.province,
                                provinceAbbrev: locationSettings.provinceAbbrev,
                              } as Address)
                            : ""
                        }`}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        } else {
          return {
            title: "Privacy Policy",
            icon: Shield,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Information Collection
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      At Manager Food, we collect the following information to
                      provide you with the best possible service:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Personal Information:</strong> Name, last name,
                        email address, phone number
                      </li>
                      <li>
                        <strong>Delivery Information:</strong> Full address,
                        special instructions
                      </li>
                      <li>
                        <strong>Payment Information:</strong> Bank card data
                        (securely processed by Stripe)
                      </li>
                      <li>
                        <strong>Usage Data:</strong> Order history, food
                        preferences
                      </li>
                      <li>
                        <strong>Technical Data:</strong> IP address, browser
                        type, geolocation data (with your consent)
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Use of Data
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>We use your personal data to:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Process and deliver your orders</li>
                      <li>Contact you regarding your order</li>
                      <li>
                        Improve our services and personalize your experience
                      </li>
                      <li>Send you important notifications</li>
                      <li>Comply with our legal obligations</li>
                      <li>Prevent fraud and ensure security</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Data Sharing
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      We never sell your personal data. We may only share your
                      information with:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Delivery Partners:</strong> To ensure the
                        delivery of your orders
                      </li>
                      <li>
                        <strong>Payment Processors:</strong> Stripe to securely
                        process your payments
                      </li>
                      <li>
                        <strong>Service Providers:</strong> For hosting,
                        analysis, and customer support
                      </li>
                      <li>
                        <strong>Legal Authorities:</strong> If required by law
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Data Security
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      We implement robust security measures to protect your
                      data:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>SSL/TLS encryption for all transmissions</li>
                      <li>Secure storage with encryption of sensitive data</li>
                      <li>Limited access to data by authorized personnel</li>
                      <li>Continuous system monitoring</li>
                      <li>Regular security audits</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Your Rights
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      In accordance with data protection laws, you have the
                      right to:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Access:</strong> Request a copy of your personal
                        data
                      </li>
                      <li>
                        <strong>Rectification:</strong> Correct inaccurate data
                      </li>
                      <li>
                        <strong>Erasure:</strong> Request the deletion of your
                        data
                      </li>
                      <li>
                        <strong>Data Portability:</strong> Receive your data in
                        a structured format
                      </li>
                      <li>
                        <strong>Objection:</strong> Object to the processing of
                        your data
                      </li>
                      <li>
                        <strong>Restriction:</strong> Request restriction of
                        processing
                      </li>
                    </ul>
                    <p className="mt-4">
                      To exercise these rights, please contact us at:{" "}
                      <strong>managerfood25@gmail.com</strong>
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Cookies and Similar Technologies
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>We use cookies to enhance your experience:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Essential Cookies:</strong> Necessary for the
                        website's operation
                      </li>
                      <li>
                        <strong>Performance Cookies:</strong> To analyze website
                        usage
                      </li>
                      <li>
                        <strong>Preference Cookies:</strong> To remember your
                        choices
                      </li>
                    </ul>
                    <p>
                      You can manage your cookie preferences in your browser
                      settings.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      For any questions regarding this privacy policy, please
                      contact us:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Manager Food</strong>
                      </p>
                      <p>Email: managerfood25@gmail.com</p>
                      <p>Phone: 581-580-5573</p>
                      <p>
                        {" "}
                        {`Address: ${
                          locationSettings !== null
                            ? formatAddress({
                                id: locationSettings.id,
                                street: locationSettings.street,
                                city: locationSettings.city,
                                country: locationSettings.country,
                                zipcode: locationSettings.postalCode,
                                latitude: locationSettings.latitude,
                                longitude: locationSettings.longitude,
                                province: locationSettings.province,
                                provinceAbbrev: locationSettings.provinceAbbrev,
                              } as Address)
                            : ""
                        }`}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        }

      case "terms":
        if (i18n.language === "fr") {
          return {
            title: "Conditions d'Utilisation",
            icon: FileText,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Acceptation des Conditions
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      En utilisant les services de Manager Food, vous acceptez
                      d'être lié par ces conditions d'utilisation. Si vous
                      n'acceptez pas ces conditions, veuillez ne pas utiliser
                      nos services.
                    </p>
                    <p>
                      Ces conditions peuvent être modifiées à tout moment. Les
                      modifications prendront effet dès leur publication sur
                      notre site.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Description du Service
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Manager Food est une plateforme de commande et de
                      livraison de cuisine africaine authentique. Nos services
                      incluent :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Commande en ligne de plats préparés</li>
                      <li>Livraison à domicile</li>
                      <li>Service de retrait sur place</li>
                      <li>Support client</li>
                      <li>Suivi de commande en temps réel</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Commandes et Paiements
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      3.1 Processus de Commande
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Les commandes sont traitées dans l'ordre de réception
                      </li>
                      <li>
                        Nous nous réservons le droit de refuser une commande
                      </li>
                      <li>
                        Les prix affichés incluent toutes les taxes applicables
                      </li>
                      <li>
                        Les frais de livraison sont calculés selon la distance
                      </li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Paiement
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Le paiement est requis au moment de la commande</li>
                      <li>
                        Nous acceptons les cartes de crédit/débit via Stripe
                      </li>
                      <li>Tous les paiements sont sécurisés et chiffrés</li>
                      <li>
                        En cas d'échec de paiement, la commande sera annulée
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Livraison
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      4.1 Zones de Livraison
                    </h3>
                    <p>
                      Nous livrons actuellement dans la région de Bathurst et
                      ses environs.
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-4">
                      4.2 Délais de Livraison
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Temps de préparation : 15-25 minutes</li>
                      <li>
                        Temps de livraison : 10-20 minutes selon la distance
                      </li>
                      <li>Délai total estimé : 25-45 minutes</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-4">
                      4.3 Responsabilités
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Vous devez être disponible à l'adresse indiquée</li>
                      <li>
                        Fournir des instructions d'accès claires si nécessaire
                      </li>
                      <li>Vérifier votre commande à la réception</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Annulations et Modifications
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Aucune modification n'est possible après le paiement de
                        la commande
                      </li>
                      <li>
                        Aucune annulation n'est possible après le paiement de la
                        commande
                      </li>
                      {/* <li>
                      Contactez immédiatement notre service client pour toute
                      modification
                    </li> */}
                      {/* <li>
                      Aucune modification n'est possible une fois la livraison
                      commencée
                    </li> */}
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Responsabilité
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Manager Food s'engage à fournir des aliments de qualité et
                      un service fiable. Cependant :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Nous ne sommes pas responsables des retards dus à des
                        circonstances exceptionnelles
                      </li>
                      <li>
                        Notre responsabilité est limitée au montant de votre
                        commande
                      </li>
                      <li>
                        Vous devez signaler tout problème dans les 24 heures
                      </li>
                      <li>
                        Les allergies alimentaires doivent être signalées lors
                        de la commande
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Propriété Intellectuelle
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Tous les contenus de notre plateforme (textes, images,
                      logos, recettes) sont protégés par le droit d'auteur et
                      appartiennent à Manager Food ou à ses partenaires.
                    </p>
                    <p>
                      Toute reproduction ou utilisation non autorisée est
                      strictement interdite.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    8. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Pour toute question concernant ces conditions
                      d'utilisation :
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Manager Food</strong>
                      </p>
                      <p>Email : managerfood25@gmail.com</p>
                      <p>Téléphone : 581-580-5573</p>
                      <p>
                        {`Adresse : ${
                          locationSettings !== null
                            ? formatAddress({
                                id: locationSettings.id,
                                street: locationSettings.street,
                                city: locationSettings.city,
                                country: locationSettings.country,
                                zipcode: locationSettings.postalCode,
                                latitude: locationSettings.latitude,
                                longitude: locationSettings.longitude,
                                province: locationSettings.province,
                                provinceAbbrev: locationSettings.provinceAbbrev,
                              } as Address)
                            : ""
                        }`}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        } else {
          return {
            title: "Terms of Use",
            icon: FileText,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Acceptance of Terms
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      By using Manager Food's services, you agree to be bound by
                      these Terms of Use. If you do not agree to these terms,
                      please do not use our services.
                    </p>
                    <p>
                      These terms may be modified at any time. Changes will take
                      effect upon their publication on our website.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Service Description
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Manager Food is a platform for ordering and delivering
                      authentic African cuisine. Our services include:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Online ordering of prepared meals</li>
                      <li>Home delivery</li>
                      <li>On-site pickup service</li>
                      <li>Customer support</li>
                      <li>Real-time order tracking</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Orders and Payments
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      3.1 Ordering Process
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Orders are processed in the order of reception</li>
                      <li>We reserve the right to refuse any order</li>
                      <li>Prices displayed include all applicable taxes</li>
                      <li>Delivery fees are calculated based on distance</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Payment
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Payment is required at the time of ordering</li>
                      <li>We accept credit/debit cards via Stripe</li>
                      <li>All payments are secured and encrypted</li>
                      <li>
                        In case of payment failure, the order will be cancelled
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Delivery
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      4.1 Delivery Zones
                    </h3>
                    <p>
                      We currently deliver within the Bathurst region and its
                      surrounding areas.
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-4">
                      4.2 Delivery Times
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Preparation time: 15-25 minutes</li>
                      <li>
                        Delivery time: 10-20 minutes depending on distance
                      </li>
                      <li>Estimated total time: 25-45 minutes</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-4">
                      4.3 Responsibilities
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>You must be available at the address provided</li>
                      <li>Provide clear access instructions if necessary</li>
                      <li>Check your order upon receipt</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Cancellations and Modifications
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        No modification is possible after the order has been
                        paid
                      </li>
                      <li>
                        No cancellation is possible after the order has been
                        paid
                      </li>
                      {/* <li>
                      Contact our customer service immediately for any modification
                    </li> */}
                      {/* <li>
                      No modification is possible once delivery has started
                    </li> */}
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Liability
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Manager Food is committed to providing quality food and
                      reliable service. However:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        We are not responsible for delays due to exceptional
                        circumstances
                      </li>
                      <li>
                        Our liability is limited to the amount of your order
                      </li>
                      <li>You must report any issue within 24 hours</li>
                      <li>
                        Food allergies must be reported when placing the order
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Intellectual Property
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      All content on our platform (texts, images, logos,
                      recipes) is protected by copyright and belongs to Manager
                      Food or its partners.
                    </p>
                    <p>
                      Any unauthorized reproduction or use is strictly
                      prohibited.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    8. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>For any questions regarding these Terms of Use:</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Manager Food</strong>
                      </p>
                      <p>Email: managerfood25@gmail.com</p>
                      <p>Phone: 581-580-5573</p>
                      <p>
                        {`Address: ${
                          locationSettings !== null
                            ? formatAddress({
                                id: locationSettings.id,
                                street: locationSettings.street,
                                city: locationSettings.city,
                                country: locationSettings.country,
                                zipcode: locationSettings.postalCode,
                                latitude: locationSettings.latitude,
                                longitude: locationSettings.longitude,
                                province: locationSettings.province,
                                provinceAbbrev: locationSettings.provinceAbbrev,
                              } as Address)
                            : ""
                        }`}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        }

      case "refund":
        if (i18n.language === "fr") {
          return {
            title: "Politique de Remboursement",
            icon: RefreshCw,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Principe Général
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Chez Manager Food, votre satisfaction est notre priorité.
                      Nous nous engageons à résoudre rapidement tout problème
                      lié à votre commande et à vous offrir une solution
                      équitable.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 font-medium">
                        ✅ Garantie de satisfaction à 100% ou remboursement
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Cas de Remboursement
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Vous avez droit à un remboursement complet dans les cas
                      suivants :
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      2.1 Remboursement Automatique
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Commande non livrée :</strong> Si votre commande
                        n'arrive pas dans les 60 minutes
                      </li>
                      <li>
                        <strong>Erreur de notre part :</strong> Commande
                        incorrecte ou incomplète
                      </li>
                      <li>
                        <strong>Problème de qualité :</strong> Nourriture
                        avariée ou non conforme
                      </li>
                      <li>
                        <strong>Annulation de notre côté :</strong> Si nous ne
                        pouvons pas honorer votre commande
                      </li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      2.2 Remboursement sur Demande
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Insatisfaction justifiée :</strong> Problème de
                        goût, température, présentation
                      </li>
                      <li>
                        <strong>Problème d'allergène :</strong> Si un allergène
                        non signalé est présent
                      </li>
                      <li>
                        <strong>Retard excessif :</strong> Livraison avec plus
                        de 30 minutes de retard
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Processus de Remboursement
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      3.1 Comment Demander un Remboursement
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ol className="list-decimal list-inside space-y-2 text-blue-800">
                        <li>
                          Contactez notre service client dans les{" "}
                          <strong>24 heures</strong>
                        </li>
                        <li>Fournissez votre numéro de commande</li>
                        <li>Décrivez le problème rencontré</li>
                        <li>Joignez des photos si nécessaire</li>
                      </ol>
                    </div>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Délais de Traitement
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Réponse :</strong> Sous 2 heures pendant nos
                        heures d'ouverture
                      </li>
                      <li>
                        <strong>Décision :</strong> Dans les 24 heures maximum
                      </li>
                      <li>
                        <strong>Remboursement :</strong> 3-5 jours ouvrables sur
                        votre carte
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Types de Compensation
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Selon la situation, nous proposons différentes solutions :
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          🔄 Remplacement
                        </h4>
                        <p className="text-orange-700 text-sm">
                          Nouvelle commande gratuite si le problème peut être
                          corrigé rapidement
                        </p>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">
                          💰 Remboursement
                        </h4>
                        <p className="text-green-700 text-sm">
                          Remboursement complet sur votre moyen de paiement
                          original
                        </p>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 mb-2">
                          🎁 Crédit
                        </h4>
                        <p className="text-purple-700 text-sm">
                          Crédit sur votre compte pour une prochaine commande
                        </p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          🎯 Remise
                        </h4>
                        <p className="text-blue-700 text-sm">
                          Remise partielle selon l'ampleur du problème
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Cas Particuliers
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    {/* <h3 className="text-lg font-semibold text-black">
                    5.1 Annulation Client
                  </h3> */}
                    {/* <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Avant préparation :</strong> Remboursement complet
                    </li>
                    <li>
                      <strong>Pendant préparation :</strong> Remboursement
                      partiel (50%)
                    </li>
                    <li>
                      <strong>Après préparation :</strong> Aucun remboursement
                    </li>
                  </ul> */}

                    <h3 className="text-lg font-semibold text-black mt-6">
                      5.1 Circonstances Exceptionnelles
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Conditions météorologiques extrêmes</li>
                      <li>Problèmes techniques majeurs</li>
                      <li>Situations d'urgence</li>
                    </ul>
                    <p>
                      Dans ces cas, nous vous contacterons proactivement pour
                      trouver une solution.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Prévention des Abus
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Pour maintenir un service de qualité pour tous nos clients
                      :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Les demandes de remboursement répétées peuvent être
                        examinées
                      </li>
                      <li>
                        Nous nous réservons le droit de refuser un remboursement
                        abusif
                      </li>
                      <li>
                        Les fausses réclamations peuvent entraîner la suspension
                        du compte
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Contact pour Remboursement
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Pour toute demande de remboursement, contactez-nous
                      immédiatement :
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Service Client Manager Food</strong>
                      </p>
                      <p>📧 Email :managerfood25@gmail.com</p>
                      <p>📱 WhatsApp : 581-580-5573</p>
                      <p>📞 Téléphone : 581-580-5573</p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                      <p className="text-yellow-800 font-medium">
                        ⚡ Réponse garantie sous 2 heures pendant nos heures
                        d'ouverture
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        } else {
          return {
            title: "Refund Policy",
            icon: RefreshCw,
            content: (
              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. General Principle
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      At Manager Food, your satisfaction is our priority. We are
                      committed to quickly resolving any issue related to your
                      order and offering you a fair solution.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 font-medium">
                        ✅ 100% Satisfaction Guarantee or Your Money Back
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Refund Cases
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      You are entitled to a full refund in the following cases:
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      2.1 Automatic Refund
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Order Not Delivered:</strong> If your order does
                        not arrive within 60 minutes
                      </li>
                      <li>
                        <strong>Error on Our Part:</strong> Incorrect or
                        incomplete order
                      </li>
                      <li>
                        <strong>Quality Issue:</strong> Spoiled or
                        non-conforming food
                      </li>
                      <li>
                        <strong>Cancellation by Us:</strong> If we are unable to
                        fulfill your order
                      </li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      2.2 Refund Upon Request
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Justified Dissatisfaction:</strong> Issue with
                        taste, temperature, presentation
                      </li>
                      <li>
                        <strong>Allergen Issue:</strong> If an unmentioned
                        allergen is present
                      </li>
                      <li>
                        <strong>Excessive Delay:</strong> Delivery more than 30
                        minutes late
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Refund Process
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <h3 className="text-lg font-semibold text-black">
                      3.1 How to Request a Refund
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ol className="list-decimal list-inside space-y-2 text-blue-800">
                        <li>
                          Contact our customer service within{" "}
                          <strong>24 hours</strong>
                        </li>
                        <li>Provide your order number</li>
                        <li>Describe the issue encountered</li>
                        <li>Attach photos if necessary</li>
                      </ol>
                    </div>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Processing Times
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Response:</strong> Within 2 hours during our
                        opening hours
                      </li>
                      <li>
                        <strong>Decision:</strong> Within a maximum of 24 hours
                      </li>
                      <li>
                        <strong>Refund:</strong> 3-5 business days back to your
                        card
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Types of Compensation
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Depending on the situation, we offer different solutions:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          🔄 Replacement
                        </h4>
                        <p className="text-orange-700 text-sm">
                          Free new order if the issue can be quickly corrected
                        </p>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">
                          💰 Refund
                        </h4>
                        <p className="text-green-700 text-sm">
                          Full refund to your original payment method
                        </p>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 mb-2">
                          🎁 Credit
                        </h4>
                        <p className="text-purple-700 text-sm">
                          Credit on your account for a future order
                        </p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          🎯 Discount
                        </h4>
                        <p className="text-blue-700 text-sm">
                          Partial discount depending on the severity of the
                          issue
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Special Cases
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    {/* <h3 className="text-lg font-semibold text-black">
                    5.1 Customer Cancellation
                  </h3> */}
                    {/* <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>
                      <strong>Before preparation:</strong> Full refund
                    </li>
                    <li>
                      <strong>During preparation:</strong> Partial refund (50%)
                    </li>
                    <li>
                      <strong>After preparation:</strong> No refund
                    </li>
                  </ul> */}

                    <h3 className="text-lg font-semibold text-black mt-6">
                      5.1 Exceptional Circumstances
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Extreme weather conditions</li>
                      <li>Major technical issues</li>
                      <li>Emergency situations</li>
                    </ul>
                    <p>
                      In these cases, we will proactively contact you to find a
                      solution.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Abuse Prevention
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>To maintain quality service for all our customers:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Repeated refund requests may be subject to review</li>
                      <li>We reserve the right to refuse an abusive refund</li>
                      <li>False claims may lead to account suspension</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Refund Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>For any refund request, contact us immediately:</p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Manager Food Customer Service</strong>
                      </p>
                      <p>📧 Email: managerfood25@gmail.com</p>
                      <p>📱 WhatsApp: 581-580-5573</p>
                      <p>📞 Phone: 581-580-5573</p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                      <p className="text-yellow-800 font-medium">
                        ⚡ Guaranteed response within 2 hours during our opening
                        hours
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        }

      case "legal":
        if (i18n.language === "fr") {
          return {
            title: "Mentions Légales",
            icon: Scale,
            content: (
              <div className="space-y-8">
                {/* <section>
                <h2 className="text-2xl font-semibold text-black mb-4">
                  1. Informations sur l'Entreprise
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-black mb-4">
                      Manager Food
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p>
                          <strong>Forme juridique :</strong> SARL
                        </p>
                        <p>
                          <strong>Capital social :</strong> 5 000 000 FCFA
                        </p>
                        <p>
                          <strong>RCCM :</strong> RC/DLA-2020-B-12345
                        </p>
                        <p>
                          <strong>NUI :</strong> M012020123456789
                        </p>
                      </div>
                      <div>
                        <p>
                          <strong>Siège social :</strong>
                        </p>
                        <p>Rue de la Liberté, Akwa</p>
                        <p>BP 1234, Bathurst</p>
                        <p>Canada</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section> */}

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          📧 Contact Général
                        </h4>
                        <p className="text-blue-700">managerfood25@gmail.com</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">
                          📞 Téléphone
                        </h4>
                        <p className="text-green-700">581-580-5573</p>
                      </div>

                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">
                          ⚖️ Questions Légales
                        </h4>
                        <p className="text-purple-700">
                          managerfood25@gmail.com
                        </p>
                      </div>

                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          🛡️ Protection des Données
                        </h4>
                        <p className="text-orange-700">
                          managerfood25@gmail.com
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Développement et Design
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Conception et développement :</strong> Canada
                        Global Web
                      </p>
                      <p>
                        <strong>Site web :</strong>{" "}
                        <a
                          target="_blank"
                          href="https://cgweb.ca"
                          className="text-orange-500 hover:text-orange-600"
                        >
                          cgweb.ca
                        </a>
                      </p>
                      <p>
                        <strong>Contact :</strong> contact@cgweb.ca
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Propriété Intellectuelle
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      L'ensemble des éléments composant le site Manager Food
                      (textes, images, vidéos, logos, graphismes, icônes, sons,
                      logiciels, etc.) sont la propriété exclusive de Manager
                      Food SARL ou de ses partenaires.
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.1 Marques et Logos
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>La marque "Manager Food" est déposée et protégée</li>
                      <li>
                        Le logo et l'identité visuelle sont protégés par le
                        droit d'auteur
                      </li>
                      <li>Toute reproduction non autorisée est interdite</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Contenu du Site
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Les recettes et descriptions de plats sont notre
                        propriété
                      </li>
                      <li>
                        Les photographies sont sous licence ou propriété de
                        Manager Food
                      </li>
                      <li>
                        Le code source et l'architecture du site sont protégés
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Données Personnelles
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Conformément à la loi Canadienne sur la protection des
                      données personnelles et au RGPD :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        Nous collectons uniquement les données nécessaires à nos
                        services
                      </li>
                      <li>
                        Vos données sont traitées de manière sécurisée et
                        confidentielle
                      </li>
                      <li>
                        Vous disposez d'un droit d'accès, de rectification et de
                        suppression
                      </li>
                      <li>
                        Notre politique de confidentialité détaille nos
                        pratiques
                      </li>
                    </ul>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-blue-800">
                        <strong>Délégué à la Protection des Données :</strong>{" "}
                        managerfood25@gmail.com
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Cookies
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Notre site utilise des cookies pour améliorer votre
                      expérience utilisateur :
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Cookies techniques :</strong> Nécessaires au
                        fonctionnement du site
                      </li>
                      <li>
                        <strong>Cookies analytiques :</strong> Pour comprendre
                        l'utilisation du site
                      </li>
                      <li>
                        <strong>Cookies de préférence :</strong> Pour mémoriser
                        vos choix
                      </li>
                    </ul>
                    <p>
                      Vous pouvez configurer votre navigateur pour refuser les
                      cookies, mais cela peut affecter le fonctionnement du
                      site.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Droit Applicable et Juridiction
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Les présentes mentions légales sont régies par le droit
                      Canadien.
                    </p>
                    <p>
                      En cas de litige, les tribunaux de Bathurst seront seuls
                      compétents, après tentative de résolution amiable.
                    </p>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">
                        <strong>Médiation :</strong> En cas de différend, nous
                        privilégions la médiation avant tout recours judiciaire.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Mise à Jour
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Ces mentions légales peuvent être modifiées à tout moment
                      pour s'adapter aux évolutions légales, réglementaires ou
                      techniques.
                    </p>
                    <p>
                      <strong>Dernière mise à jour :</strong>{" "}
                      {new Date().toLocaleDateString("fr-FR")}
                    </p>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800">
                        Les utilisateurs seront informés de toute modification
                        importante par email ou notification sur le site.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        } else {
          return {
            title: "Legal Notice",
            icon: Scale,
            content: (
              <div className="space-y-8">
                {/* <section>
                <h2 className="text-2xl font-semibold text-black mb-4">
                  1. Company Information
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-black mb-4">
                      Manager Food
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p>
                          <strong>Legal Form:</strong> SARL
                        </p>
                        <p>
                          <strong>Share Capital:</strong> 5 000 000 FCFA
                        </p>
                        <p>
                          <strong>Trade Register No. (RCCM):</strong> RC/DLA-2020-B-12345
                        </p>
                        <p>
                          <strong>Unique Identification No. (NUI):</strong> M012020123456789
                        </p>
                      </div>
                      <div>
                        <p>
                          <strong>Registered Office:</strong>
                        </p>
                        <p>Rue de la Liberté, Akwa</p>
                        <p>BP 1234, Bathurst</p>
                        <p>Canada</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section> */}

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    1. Contact
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          📧 General Contact
                        </h4>
                        <p className="text-blue-700">managerfood25@gmail.com</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">
                          📞 Phone
                        </h4>
                        <p className="text-green-700">581-580-5573</p>
                      </div>

                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">
                          ⚖️ Legal Questions
                        </h4>
                        <p className="text-purple-700">
                          managerfood25@gmail.com
                        </p>
                      </div>

                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-orange-800 mb-2">
                          🛡️ Data Protection
                        </h4>
                        <p className="text-orange-700">
                          managerfood25@gmail.com
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    2. Development and Design
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Conception and Development:</strong> Canada
                        Global Web
                      </p>
                      <p>
                        <strong>Website:</strong>{" "}
                        <a
                          target="_blank"
                          href="https://cgweb.ca"
                          className="text-orange-500 hover:text-orange-600"
                        >
                          cgweb.ca
                        </a>
                      </p>
                      <p>
                        <strong>Contact:</strong> contact@cgweb.ca
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    3. Intellectual Property
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      All elements composing the Manager Food website (texts,
                      images, videos, logos, graphics, icons, sounds, software,
                      etc.) are the exclusive property of Manager Food SARL or
                      its partners.
                    </p>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.1 Trademarks and Logos
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        The "Manager Food" brand is registered and protected
                      </li>
                      <li>
                        The logo and visual identity are protected by copyright
                      </li>
                      <li>Any unauthorized reproduction is prohibited</li>
                    </ul>

                    <h3 className="text-lg font-semibold text-black mt-6">
                      3.2 Site Content
                    </h3>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Recipes and dish descriptions are our property</li>
                      <li>Photographs are licensed or owned by Manager Food</li>
                      <li>
                        The source code and site architecture are protected
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    4. Personal Data
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      In accordance with Canadian personal data protection law
                      and the GDPR:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>We only collect data necessary for our services</li>
                      <li>
                        Your data is processed securely and confidentially
                      </li>
                      <li>
                        You have a right of access, rectification, and erasure
                      </li>
                      <li>Our privacy policy details our practices</li>
                    </ul>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-blue-800">
                        <strong>Data Protection Officer (DPO):</strong>{" "}
                        managerfood25@gmail.com
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    5. Cookies
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      Our site uses cookies to enhance your user experience:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>
                        <strong>Technical Cookies:</strong> Necessary for the
                        site's operation
                      </li>
                      <li>
                        <strong>Analytical Cookies:</strong> To understand site
                        usage
                      </li>
                      <li>
                        <strong>Preference Cookies:</strong> To remember your
                        choices
                      </li>
                    </ul>
                    <p>
                      You can configure your browser to refuse cookies, but this
                      may affect the site's operation.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    6. Applicable Law and Jurisdiction
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>These legal notices are governed by Canadian law.</p>
                    <p>
                      In the event of a dispute, the courts of Bathurst shall
                      have sole jurisdiction, after an attempt at amicable
                      resolution.
                    </p>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">
                        <strong>Mediation:</strong> In case of a dispute, we
                        favor mediation before any judicial recourse.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    7. Update
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      These legal notices may be modified at any time to adapt
                      to legal, regulatory, or technical changes.
                    </p>
                    <p>
                      <strong>Last updated:</strong>{" "}
                      {new Date().toLocaleDateString("fr-FR")}
                    </p>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800">
                        Users will be informed of any significant modification
                        by email or notification on the site.
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            ),
          };
        }

      default:
        return {
          title: "Page non trouvée",
          icon: FileText,
          content: <p>Cette page n'existe pas.</p>,
        };
    }
  };

  const { title, icon: IconComponent, content } = getPageContent();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-8">
        <button
          onClick={onBack}
          className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center">
          <IconComponent className="w-8 h-8 text-orange-500 mr-3" />
          <h1 className="text-3xl font-bold text-black">{title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-8">{content}</div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-6 bg-gray-50 rounded-b-xl">
          <div className="text-center text-sm text-gray-600">
            <p>
              Pour toute question concernant ce document, contactez-nous à :
              <a
                href="mailto:managerfood25@gmail.com"
                className="text-orange-500 hover:text-orange-600 ml-1"
              >
                managerfood25@gmail.com
              </a>
            </p>
            <p className="mt-2">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPages;
